// Phase 9B — UserIntelligencePage.
//
// CRM-style user intelligence dashboard. Shows engagement level,
// intelligence entries by category, and user notes. Premium
// Bebas Neue / JetBrains Mono / DM Sans typography.

import { useState, useMemo } from "react";
import type { CSSProperties } from "react";
import {
  useUserIntel,
  useUserEngagement,
  useUserNotes,
} from "../../intelligence/useUserIntel.ts";
import type {
  IntelCategory,
  UserIntelEntry,
  UserEngagement,
  UserNote,
} from "../../intelligence/useUserIntel.ts";
import * as s from "./intel.styles.ts";

// ─── Constants ────────────────────────────────────────────────

const CATEGORY_TABS: { key: "all" | IntelCategory; label: string }[] = [
  { key: "all", label: "All" },
  { key: "preference", label: "Preferences" },
  { key: "behavior", label: "Behavior" },
  { key: "engagement", label: "Engagement" },
  { key: "risk_profile", label: "Risk" },
  { key: "market_affinity", label: "Markets" },
  { key: "performance", label: "Performance" },
];

const LEVEL_ICONS: Record<string, string> = {
  dormant: "○",
  casual: "◔",
  regular: "◑",
  active: "◕",
  power: "●",
};

// ─── Helpers ──────────────────────────────────────────────────

function relTime(raw: string): string {
  const diff = Date.now() - new Date(raw).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${String(mins)}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${String(hrs)}h`;
  return `${String(Math.floor(hrs / 24))}d`;
}

// ─── Engagement Banner ────────────────────────────────────────

function EngagementBanner({
  engagement,
}: {
  engagement: UserEngagement | null;
}): JSX.Element {
  if (!engagement) {
    return (
      <div style={s.engagementBanner}>
        <div style={s.engagementLevel}>
          <span style={s.levelBadge}>—</span>
          <span style={s.levelLabel}>No Data</span>
        </div>
        <div style={s.engagementStats}>
          <div style={s.engStatCell}>
            <span style={s.engStatValue}>0</span>
            <span style={s.engStatLabel}>Sessions (7d)</span>
          </div>
          <div style={s.engStatCell}>
            <span style={s.engStatValue}>0</span>
            <span style={s.engStatLabel}>Actions (7d)</span>
          </div>
          <div style={s.engStatCell}>
            <span style={s.engStatValue}>0</span>
            <span style={s.engStatLabel}>Streak</span>
          </div>
          <div style={s.engStatCell}>
            <span style={s.engStatValue}>—</span>
            <span style={s.engStatLabel}>Avg Session</span>
          </div>
        </div>
      </div>
    );
  }

  const lvl = engagement.engagement_level;
  const icon = LEVEL_ICONS[lvl] ?? "○";

  return (
    <div style={s.engagementBanner}>
      <div style={s.engagementLevel}>
        <span style={s.levelBadge}>
          {icon} {lvl.toUpperCase()}
        </span>
        <span style={s.levelLabel}>Engagement Level</span>
      </div>
      <div style={s.engagementStats}>
        <div style={s.engStatCell}>
          <span style={s.engStatValue}>{String(engagement.sessions_7d)}</span>
          <span style={s.engStatLabel}>Sessions (7d)</span>
        </div>
        <div style={s.engStatCell}>
          <span style={s.engStatValue}>{String(engagement.sessions_30d)}</span>
          <span style={s.engStatLabel}>Sessions (30d)</span>
        </div>
        <div style={s.engStatCell}>
          <span style={s.engStatValue}>{String(engagement.actions_7d)}</span>
          <span style={s.engStatLabel}>Actions (7d)</span>
        </div>
        <div style={s.engStatCell}>
          <span style={s.engStatValue}>{String(engagement.streak_days)}d</span>
          <span style={s.engStatLabel}>Streak</span>
        </div>
        <div style={s.engStatCell}>
          <span style={s.engStatValue}>
            {engagement.avg_session_mins > 0
              ? `${engagement.avg_session_mins.toFixed(0)}m`
              : "—"}
          </span>
          <span style={s.engStatLabel}>Avg Session</span>
        </div>
        {engagement.favorite_market ? (
          <div style={s.engStatCell}>
            <span style={s.engStatValue}>{engagement.favorite_market}</span>
            <span style={s.engStatLabel}>Top Market</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── Intel Card ───────────────────────────────────────────────

function IntelCard({ entry }: { entry: UserIntelEntry }): JSX.Element {
  const catStyle = s.CATEGORY_STYLES[entry.category] ?? s.CATEGORY_STYLES.preference;
  const displayValue =
    entry.value_text ??
    (entry.value_num !== null ? String(entry.value_num) : "—");

  const cardBorder: CSSProperties = {
    ...s.intelCard,
    borderLeft:
      entry.confidence >= 0.8
        ? "3px solid var(--sz-success-text)"
        : entry.confidence >= 0.5
          ? "3px solid var(--sz-amber)"
          : "3px solid var(--sz-border-subtle)",
  };

  return (
    <div style={cardBorder}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={s.intelCardKey}>{entry.key.replace(/_/g, " ")}</span>
        <span style={catStyle}>{entry.category.replace(/_/g, " ")}</span>
      </div>
      <span style={s.intelCardValue}>{displayValue}</span>
      <div style={s.intelCardMeta}>
        <div style={s.confidenceBar}>
          <div
            style={{
              ...s.confidenceFill,
              width: `${(entry.confidence * 100).toFixed(0)}%`,
            }}
          />
        </div>
        <span style={s.sourceTag}>{entry.source}</span>
      </div>
    </div>
  );
}

// ─── Note Card ────────────────────────────────────────────────

function NoteCard({ note }: { note: UserNote }): JSX.Element {
  return (
    <div style={note.pinned ? { ...s.noteCard, ...s.notePinned } : s.noteCard}>
      <span style={s.noteContent}>{note.content}</span>
      <div style={s.noteMeta}>
        <span style={s.noteAuthor}>{note.author}</span>
        <span style={s.noteTime}>{relTime(note.created_at)}</span>
      </div>
      {note.tags.length > 0 ? (
        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
          {note.tags.map((t) => (
            <span key={t} style={s.noteTag}>
              {t}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export function UserIntelligencePage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<"all" | IntelCategory>("all");

  const intel = useUserIntel(activeTab === "all" ? undefined : activeTab);
  const engState = useUserEngagement();
  const notesState = useUserNotes();

  const filteredEntries = useMemo(
    () =>
      activeTab === "all"
        ? intel.entries
        : intel.entries.filter((e) => e.category === activeTab),
    [intel.entries, activeTab],
  );

  // Group by category for the "all" view
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of intel.entries) {
      counts[e.category] = (counts[e.category] ?? 0) + 1;
    }
    return counts;
  }, [intel.entries]);

  return (
    <div style={s.intelRoot}>
      {/* Header */}
      <div style={s.intelHeader}>
        <div>
          <h2 style={s.intelTitle}>User Intelligence</h2>
          <div style={s.intelSubtitle}>
            CRM foundation · behavioral signals · engagement metrics
          </div>
        </div>
        <span style={s.sectionCount}>
          {String(intel.entries.length)} signals
        </span>
      </div>

      {/* Engagement Banner */}
      <EngagementBanner engagement={engState.engagement} />

      {/* Two-column: Intel grid + Notes */}
      <div style={s.twoCol}>
        {/* Left: Intelligence entries */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sz-space-3)" }}>
          {/* Category tabs */}
          <div style={s.tabBar}>
            {CATEGORY_TABS.map((tab) => {
              const count =
                tab.key === "all"
                  ? intel.entries.length
                  : (categoryCounts[tab.key] ?? 0);
              return (
                <button
                  key={tab.key}
                  type="button"
                  style={activeTab === tab.key ? s.tabItemActive : s.tabItem}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {count > 0 ? ` (${String(count)})` : ""}
                </button>
              );
            })}
          </div>

          {/* Cards grid */}
          {filteredEntries.length === 0 ? (
            <div style={s.emptyState}>
              <span style={s.emptyIcon}>◇</span>
              <span style={s.emptyTitle}>No Intelligence Data</span>
              <span style={s.emptyNote}>
                Behavioral signals and preference data will appear here as the
                system learns from your activity.
              </span>
            </div>
          ) : (
            <div style={s.intelGrid}>
              {filteredEntries.map((entry) => (
                <IntelCard key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </div>

        {/* Right: Notes */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sz-space-3)" }}>
          <div style={s.sectionHeader}>
            <span style={s.sectionTitle}>Notes</span>
            <span style={s.sectionCount}>
              {String(notesState.notes.length)}
            </span>
          </div>

          {notesState.notes.length === 0 ? (
            <div style={s.emptyState}>
              <span style={s.emptyIcon}>▤</span>
              <span style={s.emptyTitle}>No Notes</span>
              <span style={s.emptyNote}>
                System-generated and user notes will appear here.
              </span>
            </div>
          ) : (
            <div style={s.notesColumn}>
              {notesState.notes.map((note) => (
                <NoteCard key={note.id} note={note} />
              ))}
            </div>
          )}
        </div>
      </div>

      <span style={s.phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
